import { apiService } from "./api"

interface PushSubscriptionData {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
}

class PushNotificationService {
  private registration: ServiceWorkerRegistration | null = null
  private subscription: PushSubscription | null = null
  private isInitialized = false

  /**
   * Check if push notifications are supported
   */
  public isSupported(): boolean {
    return (
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    )
  }

  /**
   * Register service worker
   */
  public async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!this.isSupported()) {
      throw new Error("Service Workers or Push API not supported")
    }

    try {
      // eslint-disable-next-line no-console
      console.log("📱 [PushNotification] Registering service worker...")
      this.registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      })

      // eslint-disable-next-line no-console
      console.log("✅ [PushNotification] Service worker registered:", this.registration.scope)

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready
      // eslint-disable-next-line no-console
      console.log("✅ [PushNotification] Service worker ready")

      return this.registration
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ [PushNotification] Error registering service worker:", error)
      throw error
    }
  }

  /**
   * Get current notification permission status
   */
  public getPermissionStatus(): NotificationPermission | null {
    if (!("Notification" in window)) {
      return null
    }
    return Notification.permission
  }

  /**
   * Check if there's an active push subscription
   */
  public async hasActiveSubscription(): Promise<boolean> {
    if (!this.isSupported()) {
      return false
    }

    try {
      // Ensure we have registration
      if (!this.registration) {
        try {
          await this.registerServiceWorker()
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn("⚠️ [PushNotification] Could not register service worker to check subscription:", error)
          return false
        }
      }

      if (!this.registration) {
        return false
      }

      // Check if there's an active subscription in PushManager
      const subscription = await this.registration.pushManager.getSubscription()
      return subscription !== null
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ [PushNotification] Error checking subscription:", error)
      return false
    }
  }

  /**
   * Request notification permission
   */
  public async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!("Notification" in window)) {
      throw new Error("Notifications not supported")
    }

    let permission = Notification.permission

    if (permission === "default") {
      // Request permission (requires user gesture on iOS)
      permission = await Notification.requestPermission()
      // eslint-disable-next-line no-console
      console.log("📱 [PushNotification] Permission requested:", permission)
    }

    return permission
  }

  /**
   * Subscribe to push notifications
   */
  public async subscribeToPush(): Promise<PushSubscription> {
    if (!this.registration) {
      await this.registerServiceWorker()
    }

    if (!this.registration) {
      throw new Error("Service worker registration failed")
    }

    try {
      // Check existing subscription
      this.subscription = await this.registration.pushManager.getSubscription()

      if (this.subscription) {
        // eslint-disable-next-line no-console
        console.log("✅ [PushNotification] Already subscribed to push notifications")
        return this.subscription
      }

      // Check permission (don't request if already denied or default)
      const permission = this.getPermissionStatus()
      if (permission !== "granted") {
        throw new Error("Notification permission not granted")
      }

      // Subscribe to push notifications
      // eslint-disable-next-line no-console
      console.log("📱 [PushNotification] Subscribing to push notifications...")

      // Get VAPID public key from backend
      let vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY

      // If not in env, fetch from backend
      if (!vapidPublicKey) {
        try {
          const response = await apiService.get<{ publicKey: string }>("/notifications/vapid-public-key")
          vapidPublicKey = response.publicKey
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("❌ [PushNotification] Error fetching VAPID public key:", error)
          throw new Error("VAPID public key not available")
        }
      }

      if (!vapidPublicKey) {
        throw new Error("VAPID public key not configured")
      }

      // Convert VAPID key from base64url to Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey) as unknown as BufferSource

      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true, // Required for all browsers
        applicationServerKey,
      })

      // eslint-disable-next-line no-console
      console.log("✅ [PushNotification] Subscribed to push notifications:", this.subscription.endpoint)

      return this.subscription
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ [PushNotification] Error subscribing to push:", error)
      throw error
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  public async unsubscribeFromPush(): Promise<boolean> {
    if (!this.subscription) {
      return false
    }

    try {
      const result = await this.subscription.unsubscribe()
      this.subscription = null
      // eslint-disable-next-line no-console
      console.log("✅ [PushNotification] Unsubscribed from push notifications")
      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ [PushNotification] Error unsubscribing:", error)
      return false
    }
  }

  /**
   * Send subscription to backend
   */
  public async sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    try {
      const subscriptionData: PushSubscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey("p256dh")!),
          auth: this.arrayBufferToBase64(subscription.getKey("auth")!),
        },
      }

      // eslint-disable-next-line no-console
      console.log("📤 [PushNotification] Sending subscription to backend...")

      await apiService.post("/notifications/subscribe", subscriptionData)

      // eslint-disable-next-line no-console
      console.log("✅ [PushNotification] Subscription sent to backend")
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ [PushNotification] Error sending subscription to backend:", error)
      throw error
    }
  }

  /**
   * Remove subscription from backend
   */
  public async removeSubscriptionFromBackend(): Promise<void> {
    try {
      // eslint-disable-next-line no-console
      console.log("📤 [PushNotification] Removing subscription from backend...")

      // Pass endpoint if available, otherwise backend will remove all subscriptions for user
      const endpoint = this.subscription?.endpoint
      await apiService.delete("/notifications/unsubscribe", endpoint ? { data: { endpoint } } : undefined)

      // eslint-disable-next-line no-console
      console.log("✅ [PushNotification] Subscription removed from backend")
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ [PushNotification] Error removing subscription from backend:", error)
      throw error
    }
  }

  /**
   * Show local notification (for testing or when app is in foreground)
   */
  public async showNotification(title: string, options: NotificationOptions = {}): Promise<void> {
    if (!this.registration) {
      await this.registerServiceWorker()
    }

    if (!this.registration) {
      // Fallback to regular Notification API
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(title, options)
      }
      return
    }

    await this.registration.showNotification(
      title,
      {
        icon: "/web-app-manifest-192x192.png",
        badge: "/web-app-manifest-192x192.png",
        // `vibrate` is not in older TS lib NotificationOptions, so omit here
        ...options,
      }
    )
  }

  /**
   * Enable push notifications manually (requires user gesture)
   */
  public async enablePushNotifications(): Promise<boolean> {
    if (!this.isSupported()) {
      throw new Error("Push notifications not supported")
    }

    try {
      // Register service worker
      await this.registerServiceWorker()

      // Request permission (this requires user gesture)
      const permission = await this.requestNotificationPermission()

      if (permission !== "granted") {
        throw new Error(`Permission denied: ${permission}`)
      }

      // Subscribe to push
      const subscription = await this.subscribeToPush()

      // Send to backend
      await this.sendSubscriptionToBackend(subscription)

      this.isInitialized = true
      // eslint-disable-next-line no-console
      console.log("✅ [PushNotification] Push notifications enabled successfully")
      return true
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ [PushNotification] Error enabling push notifications:", error)
      throw error
    }
  }

  /**
   * Disable push notifications
   */
  public async disablePushNotifications(): Promise<boolean> {
    try {
      // Ensure we have registration
      if (!this.registration) {
        try {
          await this.registerServiceWorker()
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn("⚠️ [PushNotification] Could not register service worker to unsubscribe:", error)
        }
      }

      // Check if there's an active subscription in PushManager (may be different from this.subscription)
      if (this.registration) {
        const currentSubscription = await this.registration.pushManager.getSubscription()
        if (currentSubscription) {
          try {
            await currentSubscription.unsubscribe()
            // eslint-disable-next-line no-console
            console.log("✅ [PushNotification] Unsubscribed from push notifications")
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn("⚠️ [PushNotification] Error unsubscribing from PushManager:", error)
          }
        }
      }

      // Also try to unsubscribe using stored subscription reference
      if (this.subscription) {
        try {
          await this.unsubscribeFromPush()
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn("⚠️ [PushNotification] Error unsubscribing from stored subscription:", error)
        }
      }

      // Remove from backend
      await this.removeSubscriptionFromBackend()

      // Clear local references
      this.subscription = null
      this.isInitialized = false

      // eslint-disable-next-line no-console
      console.log("✅ [PushNotification] Push notifications disabled")
      return true
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ [PushNotification] Error disabling push notifications:", error)
      throw error
    }
  }

  /**
   * Initialize push notifications (register, subscribe, send to backend)
   * Only works if permission is already granted
   */
  public async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return true
    }

    if (!this.isSupported()) {
      // eslint-disable-next-line no-console
      console.warn("⚠️ [PushNotification] Push notifications not supported")
      return false
    }

    // Check current permission status
    const permission = this.getPermissionStatus()

    // If permission is already granted, initialize automatically
    if (permission === "granted") {
      try {
        // Register service worker
        await this.registerServiceWorker()

        // Subscribe to push (will use existing subscription if available)
        const subscription = await this.subscribeToPush()

        // Send to backend
        await this.sendSubscriptionToBackend(subscription)

        this.isInitialized = true
        // eslint-disable-next-line no-console
        console.log("✅ [PushNotification] Push notifications initialized successfully")
        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("⚠️ [PushNotification] Initialization failed:", error)
        return false
      }
    }

    // If permission is "default" or "denied", don't auto-initialize
    // User must click button to enable (requires user gesture)
    // eslint-disable-next-line no-console
    console.log("ℹ️ [PushNotification] Push notifications require user permission. Use enablePushNotifications() with user gesture.")
    return false
  }

  /**
   * Cleanup - unsubscribe and remove from backend
   */
  public async cleanup(): Promise<void> {
    try {
      await this.removeSubscriptionFromBackend()
      await this.unsubscribeFromPush()
      this.isInitialized = false
      // eslint-disable-next-line no-console
      console.log("✅ [PushNotification] Push notifications cleaned up")
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ [PushNotification] Error cleaning up:", error)
    }
  }

  /**
   * Helper: Convert base64url to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }

  /**
   * Helper: Convert ArrayBuffer to base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer)
    let binary = ""
    for (let i = 0; i < bytes.byteLength; i++) {
      const byte = bytes[i]!
      binary += String.fromCharCode(byte)
    }
    return window.btoa(binary)
  }
}

export const pushNotificationService = new PushNotificationService()
